import { useEffect, useState } from "react";
import { Mail, RefreshCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

import VisitorStatsTiles from "./components/VisitorStatsTiles";
import VisitorFiltersBar from "./components/VisitorFiltersBar";
import VisitorRow from "./components/VisitorRow";
import useVisitorFilters from "./hooks/useVisitorFilters";
import { exportVisitorsCsv } from "./components/visitorCsv";

function TableBody({ loading, visitors, filtered, editing }) {
    if (loading && visitors.length === 0) {
        return <p className="p-6 text-[14px] text-[#6F6B63]">Loading visitors…</p>;
    }
    if (filtered.length === 0) {
        return (
            <div className="p-10 text-center" data-testid="visitors-empty">
                <p className="text-[14.5px] text-[#1A2230] font-medium">No visitors match.</p>
                <p className="mt-1 text-[13px] text-[#6F6B63]">
                    {visitors.length === 0
                        ? "Once visitors introduce themselves to Jerry, they'll appear here."
                        : "Try clearing the search or changing the filter."}
                </p>
            </div>
        );
    }
    return (
        <table className="w-full text-[13.5px]">
            <thead className="bg-[#F7F5F0] text-[#6F6B63] text-[11.5px] uppercase tracking-wider">
                <tr>
                    <th className="text-left px-5 py-3 font-medium">Visitor</th>
                    <th className="text-left px-5 py-3 font-medium">Role</th>
                    <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Messages</th>
                    <th className="text-left px-5 py-3 font-medium">Last seen</th>
                    <th className="text-left px-5 py-3 font-medium">Notes</th>
                </tr>
            </thead>
            <tbody>
                {filtered.map((v) => (
                    <VisitorRow
                        key={v.session_id}
                        visitor={v}
                        isEditing={editing.editingId === v.session_id}
                        isSaving={editing.savingId === v.session_id}
                        draftNote={editing.draftNote}
                        setDraftNote={editing.setDraftNote}
                        onOpenNote={editing.openNote}
                        onSaveNote={editing.saveNote}
                        onCancelNote={editing.cancel}
                    />
                ))}
            </tbody>
        </table>
    );
}

export default function AdminVisitors() {
    const [visitors, setVisitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [editingId, setEditingId] = useState(null);
    const [draftNote, setDraftNote] = useState("");
    const [savingId, setSavingId] = useState(null);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get("/admin/visitors", { params: { limit: 500 } });
            setVisitors(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openNote = (v) => {
        setEditingId(v.session_id);
        setDraftNote(v.notes || "");
    };

    const saveNote = async (sessionId) => {
        setSavingId(sessionId);
        try {
            const { data } = await api.patch(`/admin/visitors/${sessionId}`, { notes: draftNote });
            setVisitors((prev) =>
                prev.map((v) => (v.session_id === sessionId ? { ...v, ...data } : v))
            );
            setEditingId(null);
            toast.success(draftNote.trim() ? "Note saved" : "Note cleared");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSavingId(null);
        }
    };

    const { filtered, stats } = useVisitorFilters(visitors, q, roleFilter);

    const editing = {
        editingId,
        savingId,
        draftNote,
        setDraftNote,
        openNote,
        saveNote,
        cancel: () => setEditingId(null),
    };

    return (
        <div className="space-y-8" data-testid="admin-visitors">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        Visitor insights
                    </p>
                    <h1 className="mt-2 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                        Who's been chatting with Jerry.
                    </h1>
                    <p className="mt-3 text-[14.5px] text-[#6F6B63] max-w-2xl">
                        Each row is a chat session — anyone who opened the
                        chatbot and shared a name or role. Use this to follow
                        up with warm referral leads from DFPS, JPOs, foster
                        parents, and caregivers.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => exportVisitorsCsv(filtered)}
                        disabled={loading || visitors.length === 0}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] text-white hover:bg-[#08182B] text-[13px] px-3.5 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="visitors-export-csv"
                    >
                        <Download className="h-3.5 w-3.5" />
                        Export CSV
                    </button>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD3] hover:border-[#0E2A47] text-[13px] px-3.5 py-1.5 transition-colors disabled:opacity-50"
                        data-testid="visitors-refresh"
                    >
                        <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-xl bg-[#fdf6e8] border border-[#C8A24A]/30 text-[#8a6a1f] px-4 py-3 text-[13.5px]">
                    {error}
                </div>
            )}

            <VisitorStatsTiles stats={stats} />
            <VisitorFiltersBar q={q} setQ={setQ} roleFilter={roleFilter} setRoleFilter={setRoleFilter} />

            <div className="bg-white rounded-2xl border border-[#E5DFD3] overflow-hidden" data-testid="visitors-table">
                <TableBody loading={loading} visitors={visitors} filtered={filtered} editing={editing} />
            </div>

            <p className="text-[12px] text-[#6F6B63] flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Visitors don't share emails through chat. To follow up, reply
                through their referral or contact submission, or call them
                back through the channel they used.
            </p>
        </div>
    );
}
